/**
 * Section « À valider » : une carte par pièce lue et proposée, avec l'aperçu
 * du fichier, sa provenance, les champs lus (en orange quand ils sont
 * douteux), la rubrique et le nom proposés. Valider et déposer, corriger,
 * écarter avec un motif.
 */

import { dateFr, eurosCentimes } from '@scc/core';
import { h, vider } from '../../dom.js';
import {
  corrigerPiece,
  deposerPiece,
  ecarterPiece,
  ErreurPiece,
  categoriserDansZoho,
  joindreDansZoho,
  lireAvances,
  noterAvance,
  propositionsZoho,
  type DepenseZoho,
  type PropositionsZoho,
  type NatureEcartee,
  type NaturePiece,
  type Piece,
  type PieceListee,
} from './api.js';
import { apercuPiece, brut, creerRetour, deNom, lienSur, messageDe, resumeProvenance, verrouille, type Contexte } from './communs.js';
import { sectionDeCartes, type ListeCartes, type SectionCartes } from './liste-cartes.js';
import { blocCategoriser, libelleDepense, resumeDepense, type BlocCategoriser } from './pieces-zoho.js';
import {
  correctionDepuis,
  infobulleChamp,
  messageDepotRefuse,
  MOTIFS_PAR_DEFAUT,
  NATURES,
  type SaisiePiece,
} from './pieces-textes.js';

type Controle = HTMLButtonElement | HTMLInputElement | HTMLSelectElement;

/** Un champ du formulaire d'une pièce : orange et annoncé « à vérifier » quand la lecture est douteuse, provenance en infobulle. */
function champPiece(libelle: string, controle: Controle, douteux: boolean, infobulle: string): HTMLElement {
  controle.title = infobulle;
  return h(
    'label',
    { classe: douteux ? 'champ-piece douteux' : 'champ-piece', title: infobulle },
    libelle,
    douteux ? h('span', { classe: 'invisible' }, ' (lecture douteuse, à vérifier)') : null,
    controle,
  );
}

/** Formulaire d'une pièce : sa valeur initiale, ses contrôles, et la saisie courante. */
function formulairePiece(p: Piece, ctx: Contexte) {
  const c = p.champs;
  const texte = (valeur: string | null | undefined, max = 200): HTMLInputElement =>
    h('input', { type: 'text', maxlength: max, value: valeur ?? '' });

  const nature = h('select', {}, ...(Object.keys(NATURES) as NaturePiece[]).map((n) => h('option', { value: n }, NATURES[n])));
  nature.value = p.nature;
  const fournisseur = texte(c?.fournisseur.valeur);
  const date = h('input', { type: 'date', value: c?.date.valeur ?? '' });
  const montantLu = c?.montant.valeur ?? null;
  const montant = h('input', { type: 'text', inputmode: 'decimal', value: montantLu === null ? '' : montantLu.toFixed(2).replace('.', ',') });
  const devise = h('input', { type: 'text', maxlength: 3, value: c?.devise.valeur ?? '' });
  const numero = texte(c?.numero.valeur, 100);
  const rubrique = h('select', {}, h('option', { value: '' }, 'Aucune : à choisir'));
  if (p.rubrique !== null) rubrique.appendChild(h('option', { value: p.rubrique }, `Rubrique ${p.rubrique}`));
  rubrique.value = p.rubrique ?? '';
  const nom = texte(p.nomPropose, 150);
  nom.classList.add('champ-nom');

  // Les rubriques du Drive arrivent après la carte : elles remplacent l'option provisoire.
  void ctx
    .rubriques(p)
    .then((liste) => {
      const choix = rubrique.value;
      while (rubrique.options.length > 1) rubrique.remove(1);
      for (const r of liste) rubrique.appendChild(h('option', { value: r.numero }, brut(`${r.numero}. ${r.libelle}`)));
      if (choix !== '' && !liste.some((r) => r.numero === choix)) rubrique.appendChild(h('option', { value: choix }, `Rubrique ${choix}`));
      rubrique.value = choix;
    })
    .catch(() => {
      /* La liste reste celle de la rubrique proposée : le serveur refusera une rubrique inconnue. */
    });

  const el = h(
    'div',
    { classe: 'champs-piece' },
    champPiece('Nature', nature, false, 'Ce que la pièce est. Une nature autre que « Facture reçue par le SCC » ne permet pas le dépôt.'),
    champPiece('Fournisseur', fournisseur, c?.fournisseur.douteux ?? false, infobulleChamp(c?.fournisseur)),
    champPiece('Date de la facture', date, c?.date.douteux ?? false, infobulleChamp(c?.date)),
    champPiece('Montant TTC', montant, c?.montant.douteux ?? false, infobulleChamp(c?.montant)),
    champPiece('Devise', devise, c?.devise.douteux ?? false, infobulleChamp(c?.devise)),
    champPiece('Numéro', numero, c?.numero.douteux ?? false, infobulleChamp(c?.numero)),
    champPiece('Rubrique du Drive', rubrique, false, p.rubrique === null ? 'Aucune rubrique proposée : à choisir.' : 'Rubrique proposée.'),
    champPiece('Nom du fichier dans le Drive', nom, false, 'Nom proposé : AAAAMMJJ_Fournisseur_Numero.'),
  );
  const controles: Controle[] = [nature, fournisseur, date, montant, devise, numero, rubrique, nom];
  const saisie = (): SaisiePiece => ({
    nature: nature.value as NaturePiece,
    fournisseur: fournisseur.value,
    date: date.value,
    montant: montant.value,
    devise: devise.value,
    numero: numero.value,
    rubrique: rubrique.value,
    nom: nom.value,
  });
  return { el, controles, saisie };
}

const NATURES_ECART: readonly NatureEcartee[] = ['autre_structure', 'facture_emise', 'pas_une_facture'];

/** Propositions Zoho de la section, lues une fois et relues après une correction. */
interface SourceZoho {
  lire(): Promise<PropositionsZoho | null>;
  rafraichir(): void;
}

function sourceZoho(): SourceZoho {
  let lue: Promise<PropositionsZoho | null> | null = null;
  return {
    lire() {
      // Zoho injoignable ou droit absent : la carte se valide comme avant, sans jonction.
      lue ??= propositionsZoho().catch(() => null);
      return lue;
    },
    rafraichir() {
      lue = null;
    },
  };
}

/** Personnes à proposer comme auteur d'une avance, lues une fois pour la section. */
let personnesLues: Promise<readonly string[]> | null = null;
const personnesAvance = (): Promise<readonly string[]> => {
  personnesLues ??= lireAvances()
    .then((a) => a.personnes)
    .catch(() => {
      personnesLues = null;
      return [];
    });
  return personnesLues;
};

const AUTRE = '__autre__';

/**
 * Choix « Payée par » d'une carte : le SCC (compte, carte, prélèvement) ou une
 * personne qui a avancé la facture. Enregistré dès qu'il change.
 */
function choixAvance(
  p: Piece,
  retour: ReturnType<typeof creerRetour>,
  surChangement: (par: string | null) => void,
  rechargerAvances?: () => void,
): { el: HTMLElement; controles: (HTMLSelectElement | HTMLInputElement)[] } {
  const select = h('select', { 'aria-label': 'Qui a payé cette facture' }, h('option', { value: '' }, 'Le SCC (compte, carte ou prélèvement)'));
  const autre = h('input', { type: 'text', maxlength: 100, placeholder: 'Nom de la personne', hidden: true });
  const ajouter = (nom: string): void => {
    if (![...select.options].some((o) => o.value === nom)) select.insertBefore(h('option', { value: nom }, brut(`Avance ${deNom(nom)}`)), select.lastChild);
  };
  select.appendChild(h('option', { value: AUTRE }, 'Avance d’une autre personne…'));
  if (p.avancePar !== null) ajouter(p.avancePar);
  select.value = p.avancePar ?? '';
  void personnesAvance().then((noms) => {
    const v = select.value;
    for (const n of noms) ajouter(n);
    select.value = v;
  });

  let enregistre = p.avancePar;
  const enregistrer = (par: string | null): void => {
    void (async () => {
      try {
        await verrouille([select, autre], () => noterAvance(p.id, par));
        enregistre = par;
        personnesLues = null;
        if (par !== null) ajouter(par);
        select.value = par ?? '';
        autre.hidden = true;
        retour.ok(par === null ? 'Payée par le SCC.' : `Avance ${deNom(par)} : à rembourser (section « Avances à rembourser »).`);
        surChangement(par);
        rechargerAvances?.();
      } catch (e) {
        select.value = enregistre ?? '';
        retour.ko(messageDe(e, 'Le payeur n’a pas pu être enregistré.'));
      }
    })();
  };
  select.addEventListener('change', () => {
    if (select.value === AUTRE) {
      autre.hidden = false;
      autre.focus();
      return;
    }
    autre.hidden = true;
    enregistrer(select.value === '' ? null : select.value);
  });
  autre.addEventListener('change', () => {
    const nom = autre.value.trim();
    if (nom !== '') enregistrer(nom);
  });
  return { el: h('label', { classe: 'champ-piece champ-avance' }, 'Payée par', select, autre), controles: [select, autre] };
}

/** Ce que la carte enverra dans Zoho après le dépôt : la dépense choisie, si la case est cochée. */
interface ChoixZoho {
  readonly el: HTMLElement;
  /** Une avance de frais n'a pas de dépense du SCC : le bloc se masque et rien n'est joint. */
  masquer(oui: boolean): void;
  /**
   * Ce que la carte enverra après le dépôt, avec le montant et la date de la facture vus à la
   * proposition : joindre à une dépense, ou catégoriser une ligne de relevé puis joindre ; `null` : rien.
   */
  choix():
    | { type: 'joindre'; depense: DepenseZoho; montant: number | null; date: string | null }
    | { type: 'categoriser'; ligne: DepenseZoho; compte: string; montant: number | null; date: string | null }
    | null;
  readonly controles: (HTMLInputElement | HTMLSelectElement)[];
}

/**
 * Bloc « Joindre aussi dans Zoho » d'une carte à valider : la dépense sans pièce
 * proposée pour cette facture, cochée d'office quand une seule correspond.
 */
function choixZoho(pieceId: number, zoho: SourceZoho, surChangement: () => void): ChoixZoho {
  const el = h('div', { classe: 'zoho-carte' });
  const case_ = h('input', { type: 'checkbox' });
  const select = h('select', { 'aria-label': 'Dépense Zoho à laquelle joindre la pièce' });
  const detail = h('div', {});
  let offertes: readonly DepenseZoho[] = [];
  let vue: { montant: number | null; date: string | null } = { montant: null, date: null };
  let ouvert = false;
  let masque = false;
  let cat: BlocCategoriser | null = null;
  const controles: (HTMLInputElement | HTMLSelectElement)[] = [case_, select];

  const montrer = (): void => {
    vider(detail);
    const d = offertes.find((x) => x.id === select.value);
    if (d) detail.appendChild(resumeDepense(d));
  };
  select.addEventListener('change', montrer);
  case_.addEventListener('change', surChangement);

  void zoho.lire().then((z) => {
    vider(el);
    if (z === null) return;
    const prop = z.propositions.find((x) => x.piece.id === pieceId);
    if (!prop) return;
    ouvert = z.actif && z.scopeEcriture === true;
    vue = { montant: prop.piece.montant, date: prop.piece.date };
    const devise = (prop.piece.devise ?? 'EUR').toUpperCase();
    if (devise !== 'EUR') {
      el.appendChild(
        h(
          'p',
          { classe: 'note-lecture' },
          brut(`Facture en ${devise} : gardez le montant en ${devise}. La banque débite des euros, mais son libellé porte le montant en ${devise} (« … 16,50 USD WASABI … ») : c’est par lui que la dépense est retrouvée.`),
        ),
      );
    }
    if (prop.qualite === 'aucune') {
      if (prop.aCategoriser.length > 0) {
        cat = blocCategoriser(prop.aCategoriser, ouvert, z.scopeCategoriser, true, surChangement);
        controles.push(...cat.controles);
        el.appendChild(cat.el);
      } else {
        el.appendChild(h('p', { classe: 'note-lecture' }, 'Aucune ligne bancaire au même montant à ces dates : la pièce sera seulement déposée dans le Drive (payée par une autre voie, ou avance d’une personne ?).'));
      }
      return;
    }
    if (!ouvert) {
      el.appendChild(h('p', { classe: 'note-lecture' }, 'Une dépense Zoho correspond, mais l’envoi dans Zoho n’est pas ouvert : joignez-la ensuite dans « À joindre dans Zoho ».'));
      return;
    }
    offertes = prop.candidates;
    for (const d of offertes) select.appendChild(h('option', { value: d.id }, brut(libelleDepense(d))));
    select.hidden = offertes.length < 2;
    case_.checked = prop.qualite === 'sure';
    el.append(
      h(
        'label',
        { classe: 'case' },
        case_,
        prop.qualite === 'sure' ? ' Joindre aussi dans Zoho, à cette dépense :' : ` Joindre aussi dans Zoho : ${String(offertes.length)} dépenses au même montant, choisissez la bonne`,
      ),
      select,
      detail,
    );
    montrer();
    surChangement();
  });

  return {
    el,
    controles,
    masquer(oui) {
      masque = oui;
      el.hidden = oui;
      surChangement();
    },
    choix() {
      if (masque || !ouvert) return null;
      if (cat !== null) {
        const c = cat.choix();
        return c === null ? null : { type: 'categoriser', ligne: c.ligne, compte: c.compte, ...vue };
      }
      if (!case_.checked) return null;
      const d = offertes.find((x) => x.id === select.value);
      return d ? { type: 'joindre', depense: d, ...vue } : null;
    },
  };
}

/** Carte d'une pièce à valider. `message` : le retour à montrer d'emblée (corrections enregistrées). */
function carteValider(initiale: PieceListee, liste: ListeCartes, ctx: Contexte, zoho: SourceZoho, message?: string): HTMLElement {
  let courante: PieceListee = initiale;
  const form = formulairePiece(courante, ctx);
  const retour = creerRetour();
  if (message !== undefined) retour.ok(message);

  const valider = h('button', { type: 'button', classe: 'btn-action principal' }, 'Valider et déposer');
  const blocZoho = choixZoho(courante.id, zoho, () => {
    const c = blocZoho.choix();
    valider.textContent = c === null ? 'Valider et déposer' : c.type === 'joindre' ? 'Valider, déposer et joindre dans Zoho' : 'Valider, déposer, catégoriser et joindre dans Zoho';
  });
  const avance = choixAvance(
    courante,
    retour,
    (par) => {
      courante = { ...courante, avancePar: par };
      blocZoho.masquer(par !== null);
    },
    () => {
      ctx.recharger('avances', 'zoho');
    },
  );
  if (courante.avancePar !== null) blocZoho.masquer(true);
  const corriger = h('button', { type: 'button', classe: 'btn-action' }, 'Enregistrer les corrections');
  const ecarter = h('button', { type: 'button', classe: 'btn-action' }, 'Écarter…');

  // Écart : la nature (facultative) et le motif (obligatoire).
  const natureEcart = h(
    'select',
    {},
    h('option', { value: '' }, 'Sans préciser la nature'),
    ...NATURES_ECART.map((n) => h('option', { value: n }, NATURES[n])),
  );
  const motif = h('input', { type: 'text', maxlength: 300, placeholder: 'Pourquoi cette pièce n’est pas à classer', classe: 'champ-motif' });
  const confirmer = h('button', { type: 'button', classe: 'btn-action' }, 'Confirmer l’écart');
  const annuler = h('button', { type: 'button', classe: 'btn-action' }, 'Annuler');
  const formEcart = h(
    'div',
    { classe: 'ecart-piece', hidden: true },
    h('label', {}, 'Nature ', natureEcart),
    h('label', {}, 'Motif ', motif),
    confirmer,
    annuler,
  );
  natureEcart.addEventListener('change', () => {
    const n = natureEcart.value as NatureEcartee | '';
    if (n !== '' && motif.value.trim() === '') motif.value = MOTIFS_PAR_DEFAUT[n];
  });

  const tous = [...form.controles, ...avance.controles, ...blocZoho.controles, valider, corriger, ecarter, natureEcart, motif, confirmer, annuler];

  /** Enregistre ce qui a changé ; rend la pièce à jour, ou `null` après avoir signalé le refus. */
  const enregistrer = async (): Promise<Piece | null> => {
    const r = correctionDepuis(courante, form.saisie());
    if ('erreur' in r) {
      retour.ko(r.erreur);
      return null;
    }
    if (Object.keys(r.correction).length === 0) return courante;
    const { piece } = await corrigerPiece(courante.id, r.correction);
    courante = { ...courante, ...piece };
    return piece;
  };

  const carte = h(
    'article',
    { classe: 'carte-piece', 'aria-label': `Pièce ${String(courante.id)}` },
    h('div', { classe: 'carte-apercu' }, apercuPiece(courante)),
    h(
      'div',
      { classe: 'carte-corps' },
      resumeProvenance(courante.provenance, Math.max(0, courante.nbProvenances - 1)),
      courante.motif === null ? null : h('p', { classe: 'note-lecture' }, brut(`Remarque : ${courante.motif}`)),
      form.el,
      avance.el,
      blocZoho.el,
      h('div', { classe: 'barre-actions' }, valider, corriger, ecarter),
      formEcart,
      retour.el,
    ),
  );

  corriger.addEventListener('click', () => {
    void (async () => {
      try {
        retour.effacer();
        const avant = courante;
        const piece = await verrouille(tous, enregistrer);
        if (piece === null) return;
        if (piece === avant) {
          retour.ok('Rien n’a changé.');
          return;
        }
        // Un montant ou une date corrigés changent la dépense proposée dans Zoho.
        zoho.rafraichir();
        liste.remplacer(courante.id, carteValider(courante, liste, ctx, zoho, 'Corrections enregistrées.'));
        ctx.recharger('zoho');
      } catch (e) {
        retour.ko(messageDe(e, 'Les corrections n’ont pas pu être enregistrées.'));
      }
    })();
  });

  valider.addEventListener('click', () => {
    void (async () => {
      try {
        retour.effacer();
        const choix = blocZoho.choix();
        const depot = await verrouille(tous, async () => {
          if ((await enregistrer()) === null) return null;
          return deposerPiece(courante.id);
        });
        if (depot === null) return;
        liste.retirer(courante.id);
        const drive: (Node | string)[] = [
          depot.dejaPresent ? 'Ce fichier était déjà dans le Drive : la pièce y est rattachée. ' : 'Pièce déposée dans le Drive : ',
          brut(depot.nom),
          ' ',
          lienSur(depot.lien, 'Ouvrir ↗', 'Ouvrir le fichier dans le Drive'),
        ];
        ctx.retour.ok(...drive, await joindreApresDepot(courante, choix));
        zoho.rafraichir();
        ctx.recharger('zoho', 'avances', 'historique');
      } catch (e) {
        if (e instanceof ErreurPiece) {
          retour.ko(messageDepotRefuse(e.motif, e.message));
          // La pièce a changé d'état entre-temps : la liste se met à jour.
          if (e.statut === 409) ctx.recharger('valider', 'arbitrer', 'zoho', 'historique');
        } else {
          retour.ko('Le dépôt n’a pas abouti. Réessayez.');
        }
      }
    })();
  });

  ecarter.addEventListener('click', () => {
    formEcart.hidden = false;
    motif.focus();
  });
  annuler.addEventListener('click', () => {
    formEcart.hidden = true;
  });
  confirmer.addEventListener('click', () => {
    const m = motif.value.trim();
    if (m === '') {
      retour.ko('Le motif est obligatoire : il reste dans l’historique.');
      return;
    }
    void (async () => {
      try {
        retour.effacer();
        const n = natureEcart.value as NatureEcartee | '';
        await verrouille(tous, () => ecarterPiece(courante.id, m, n === '' ? undefined : n));
        liste.retirer(courante.id);
        ctx.retour.ok('Pièce écartée. Elle reste dans l’historique : on peut la reprendre.');
        ctx.recharger('zoho', 'avances', 'historique');
      } catch (e) {
        retour.ko(messageDe(e, 'La pièce n’a pas pu être écartée.'));
      }
    })();
  });

  return carte;
}

/**
 * Après le dépôt, joint la pièce dans Zoho si la case était cochée ; rend le
 * texte qui complète le retour. Un montant, une devise ou une date corrigés sur
 * la carte changent la dépense : elle est alors recherchée à nouveau avec les
 * valeurs corrigées, et la pièce n'est jointe que si la recherche aboutit sans
 * ambiguïté (une seule dépense, ou une seule ligne à catégoriser au compte proposé).
 */
async function joindreApresDepot(p: Piece, choix: ReturnType<ChoixZoho['choix']>): Promise<string> {
  if (choix === null) return '';
  const c = p.champs;
  if ((c?.montant.valeur ?? null) !== choix.montant || (c?.date.valeur ?? null) !== choix.date) {
    let z: Awaited<ReturnType<typeof propositionsZoho>>;
    try {
      z = await propositionsZoho();
    } catch {
      return ' Montant ou date corrigés : la dépense Zoho n’a pas pu être recherchée à nouveau, la pièce attend dans « À joindre dans Zoho ».';
    }
    const prop = z.propositions.find((x) => x.piece.id === p.id);
    const seule = prop?.qualite === 'sure' ? prop.candidates[0] : undefined;
    const ligne = prop?.qualite === 'aucune' && prop.aCategoriser.length === 1 ? prop.aCategoriser[0] : undefined;
    if (seule) choix = { type: 'joindre', depense: seule, montant: c?.montant.valeur ?? null, date: c?.date.valeur ?? null };
    else if (ligne?.compte && z.scopeCategoriser === true) choix = { type: 'categoriser', ligne, compte: ligne.compte.id, montant: c?.montant.valeur ?? null, date: c?.date.valeur ?? null };
    else return ' Montant ou date corrigés : aucune dépense Zoho ne correspond sans ambiguïté aux valeurs corrigées, la pièce attend dans « À joindre dans Zoho ».';
  }
  try {
    if (choix.type === 'categoriser') {
      await categoriserDansZoho(p.id, choix.ligne.id, choix.compte);
      return ` Ligne du ${dateFr(choix.ligne.date)} (${eurosCentimes(choix.ligne.montant)}) catégorisée dans Zoho, et pièce jointe à la dépense.`;
    }
    await joindreDansZoho(p.id, choix.depense.id);
    return ` Jointe dans Zoho à la dépense du ${dateFr(choix.depense.date)} (${eurosCentimes(choix.depense.montant)}).`;
  } catch (e) {
    return ` Mais Zoho n’a pas tout reçu (${messageDe(e, 'erreur inconnue')}) : la pièce attend dans « À joindre dans Zoho ».`;
  }
}

export function sectionValider(ctx: Contexte): SectionCartes {
  const zoho = sourceZoho();
  const s = sectionDeCartes(
    1,
    'À valider',
    'Lues et proposées : vérifiez, corrigez si besoin, puis déposez dans le Drive et joignez dans Zoho',
    'a_valider',
    (p, liste) => carteValider(p, liste, ctx, zoho),
    'Aucune pièce à valider.',
    h('p', { classe: 'note-lecture' }, 'Les champs en orange ont été lus avec un doute. Survolez un champ pour savoir d’où vient sa valeur.'),
  );
  return {
    ...s,
    charger() {
      // Les dépenses Zoho proposées se relisent avec la liste : une campagne ou une jonction les a changées.
      zoho.rafraichir();
      return s.charger();
    },
  };
}
